import { Calendar, GitBranch, ListChecks, Tag, Repeat, MapPin, Bell, Paperclip } from "lucide-react";
import { cn } from "../lib/utils";
import { recurrenceLabel, recurrenceColor, normaliseRecurrence } from "../lib/recurrence";

const PRIORITY_STYLE = {
  urgent: "bg-red-500/10 text-red-500 border-red-500/30",
  important: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  normal: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  low: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

export default function TaskCard({ task, onClick, draggable, onDragStart, onDragEnd, workspaceColor }) {
  const subtasks = task.subtasks || [];
  const doneCount = subtasks.filter((s) => s.done).length;
  const tags = task.tags || [];
  const accent = workspaceColor || null;
  const rule = normaliseRecurrence(task.recurrence, task.recurring);
  const recLabel = rule ? recurrenceLabel(rule) : "";
  const recColor = rule ? recurrenceColor(rule) : null;
  const reminderCount = (task.due_times?.length || 0) || (task.due_time ? 1 : 0);
  const hasLocation = !!(task.location && (task.location.name || task.location.address || task.location.latitude));
  const attachmentCount = task.attachment_count || 0;

  return (
    <button
      type="button"
      data-testid={`task-card-${task.id}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onClick && onClick(task)}
      className="task-card relative w-full text-left bg-card border border-border rounded-lg p-4 hover:border-primary/40 cursor-grab active:cursor-grabbing focus:outline-none focus:ring-1 focus:ring-primary overflow-hidden"
      style={accent ? { borderLeft: `3px solid ${accent}` } : {}}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className={cn(
            "text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border",
            PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.normal
          )}
        >
          {task.priority}
        </span>
        {task.due_date && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar size={11} strokeWidth={1.7} />
            {task.due_date}
            {task.due_time ? ` · ${task.due_time}` : ""}
          </span>
        )}
      </div>

      <h4 className="font-display font-semibold text-[15px] leading-snug mb-2 line-clamp-2">
        {task.title}
      </h4>

      {task.description && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{task.description}</p>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          {subtasks.length > 0 && (
            <span className="flex items-center gap-1">
              <ListChecks size={12} strokeWidth={1.7} />
              {doneCount}/{subtasks.length}
            </span>
          )}
          {reminderCount > 1 && (
            <span className="flex items-center gap-1" title={`${reminderCount} reminder times`}>
              <Bell size={11} strokeWidth={1.8} />
              {reminderCount}
            </span>
          )}
          {hasLocation && (
            <span className="flex items-center gap-1" title={task.location?.name || task.location?.address || "Location set"}>
              <MapPin size={11} strokeWidth={1.8} />
            </span>
          )}
          {attachmentCount > 0 && (
            <span
              className="flex items-center gap-1"
              data-testid={`task-attachments-${task.id}`}
              title={`${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"}`}
            >
              <Paperclip size={11} strokeWidth={1.8} />
              {attachmentCount}
            </span>
          )}
          {rule && recLabel && (
            <span
              data-testid={`task-recurrence-${task.id}`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border"
              style={{
                color: recColor,
                borderColor: `${recColor}40`,
                backgroundColor: `${recColor}1A`,
              }}
            >
              <Repeat size={10} strokeWidth={2.2} />
              {recLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end max-w-[60%]">
          {tags.slice(0, 2).map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground"
            >
              <Tag size={9} strokeWidth={1.8} />
              {t}
            </span>
          ))}
          {tags.length > 2 && <span className="text-[10px]">+{tags.length - 2}</span>}
        </div>
      </div>
    </button>
  );
}
